/**
 * AdminSystemHistoryPage — audit trail of System Health transitions.
 *
 * Fabrizio CR 01/07/2026 — Post-mortem & incident management view:
 *   • Current active state at a glance ("System is OK since 2h 15m")
 *   • Timeline of all recorded state changes in the selected window
 *     (default: 30 days, adjustable to 7 / 30 / 90 / 365)
 *   • Aggregate KPIs: time spent in OK vs WARN vs ERROR,
 *     transition counts per target status
 *   • Grouped by day for easy scanning
 *
 * Endpoint: ``GET /api/admin/system/health-checks/history?days=30``
 * (super_admin only).
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Activity, TrendingUp, ChevronRight } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { SlaReportCard } from "../components/admin/SlaReportCard";

const RANGE_OPTIONS = [
  { days: 7,   label: "7 giorni" },
  { days: 30,  label: "30 giorni" },
  { days: 90,  label: "90 giorni" },
  { days: 365, label: "1 anno" },
];

const STATUS_STYLE = {
  ok:    { color: "#10b981", bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.30)", label: "OK" },
  warn:  { color: "#f59e0b", bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.30)", label: "WARN" },
  error: { color: "#ef4444", bg: "rgba(239,68,68,0.10)",  border: "rgba(239,68,68,0.30)",  label: "ERROR" },
  unknown:{color: "#6b7280", bg: "rgba(107,114,128,0.10)", border: "rgba(107,114,128,0.30)", label: "—" },
};

// Format a raw seconds value as a compact human string. Chosen for
// scannability of the incident timeline (weeks & months collapse to
// hours+minutes to avoid overwhelming the eye).
const formatDuration = (seconds) => {
  if (seconds == null || seconds < 0) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ${seconds % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
};

// Return e.g. "01 lug 2026, 14:22" — Italian locale, no seconds
// (the timestamp precision is minute-level for the audit view).
const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const formatDateOnly = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return iso; }
};

export default function AdminSystemHistoryPage() {
  const { getAuthHeader, user } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/admin/system/health-checks/history?days=${days}`, {
        headers: getAuthHeader().headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message || "Errore caricamento cronologia");
    } finally {
      setLoading(false);
    }
  }, [days, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  // Group transitions by calendar day (Europe/Rome) for the timeline
  // rendering. The API already returns them newest-first, so we just
  // fold them into a Map keyed by ``YYYY-MM-DD``.
  const grouped = useMemo(() => {
    if (!data?.transitions) return [];
    const buckets = new Map();
    for (const t of data.transitions) {
      const dayKey = (t.to_ts || "").slice(0, 10);
      if (!buckets.has(dayKey)) buckets.set(dayKey, []);
      buckets.get(dayKey).push(t);
    }
    return Array.from(buckets.entries());
  }, [data]);

  const currentStyle = STATUS_STYLE[data?.current?.status] || STATUS_STYLE.unknown;

  if (user?.role !== "super_admin") {
    return (
      <div className="p-8 text-center text-gray-400" data-testid="admin-system-history-forbidden">
        Accesso riservato ai super admin.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto text-gray-200" data-testid="admin-system-history-page">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate("/admin")}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
          data-testid="back-to-system"
        >
          <ArrowLeft size={16} /> Torna alla Dashboard
        </button>
        <div className="flex gap-2">
          {RANGE_OPTIONS.map(({ days: d, label }) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                days === d
                  ? "bg-lojack-blue-500/20 border-lojack-blue-500/50 text-white"
                  : "bg-[#0d0d0d] border-gray-800 text-gray-400 hover:text-gray-200"
              }`}
              data-testid={`range-${d}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
        <Activity className="text-lojack-blue-400" size={30} />
        Cronologia Incidenti System Health
      </h1>
      <p className="text-gray-500 text-sm mb-8 max-w-3xl">
        Audit trail di ogni cambio di stato osservato in Produzione, utile per post-mortem con LoJack e trend analysis.
        Gli ambienti Preview/UAT sono esclusi dal tracking (dati intenzionalmente rumorosi).
      </p>

      {/* ── Report SLA mensile (req. Fabrizio 13/07/2026) ───── */}
      <SlaReportCard />

      {/* ── Current state banner ────────────────────────────── */}
      <div
        className="rounded-2xl p-5 mb-6 border"
        style={{ background: currentStyle.bg, borderColor: currentStyle.border }}
        data-testid="history-current-state"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div
              className="h-3 w-3 rounded-full"
              style={{
                background: currentStyle.color,
                boxShadow: data?.current?.status === "error" ? `0 0 12px ${currentStyle.color}` : "none",
              }}
            />
            <div>
              <div className="text-xs uppercase tracking-wider" style={{ color: currentStyle.color }}>
                Stato attuale
              </div>
              <div className="text-2xl font-bold">{currentStyle.label}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-gray-500">Durata corrente</div>
            <div className="text-xl font-mono">{formatDuration(data?.current?.duration_seconds)}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              da {formatDateTime(data?.current?.since_ts)}
            </div>
          </div>
        </div>
        {data?.current?.categories?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {data.current.categories.map(c => (
              <span
                key={c}
                className="px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide border"
                style={{ color: currentStyle.color, borderColor: currentStyle.border }}
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Aggregate KPIs ─────────────────────────────────── */}
      {data?.aggregate && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-testid="history-aggregates">
          <div className="bg-[#0d0d0d] border border-gray-800 rounded-xl p-4">
            <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">Transizioni totali</div>
            <div className="text-2xl font-bold text-white flex items-center gap-2">
              <TrendingUp size={18} className="text-lojack-blue-400" />
              {data.aggregate.total_transitions}
            </div>
            <div className="text-xs text-gray-500 mt-1">Ultimi {days} giorni</div>
          </div>
          {["ok", "warn", "error"].map(status => {
            const s = STATUS_STYLE[status];
            return (
              <div
                key={status}
                className="rounded-xl p-4 border"
                style={{ background: s.bg, borderColor: s.border }}
              >
                <div className="text-xs uppercase tracking-wider mb-1" style={{ color: s.color }}>
                  Tempo in {s.label}
                </div>
                <div className="text-2xl font-bold font-mono" style={{ color: s.color }}>
                  {formatDuration(data.aggregate.time_in_status_seconds?.[status])}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {data.aggregate.by_status?.[status] || 0} entrate in stato
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Timeline grouped by day ────────────────────────── */}
      <div className="space-y-6" data-testid="history-timeline">
        {loading && (
          <div className="text-center py-12 text-gray-500">Caricamento cronologia…</div>
        )}
        {error && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-red-300">
            {error}
          </div>
        )}
        {!loading && !error && grouped.length === 0 && (
          <div className="text-center py-16 border border-dashed border-gray-800 rounded-xl">
            <Clock size={40} className="mx-auto mb-3 text-gray-700" />
            <div className="text-gray-500 text-sm">
              Nessun cambio di stato registrato negli ultimi {days} giorni.
            </div>
            <div className="text-gray-600 text-xs mt-1">
              Se il sistema è appena stato deployato in Produzione, la cronologia si popolerà con il primo cambio.
            </div>
          </div>
        )}
        {!loading && grouped.map(([day, items]) => (
          <div key={day} data-testid={`history-day-${day}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-px flex-1 bg-gray-800" />
              <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                {formatDateOnly(day + "T12:00:00Z")}
              </div>
              <div className="h-px flex-1 bg-gray-800" />
            </div>
            <div className="space-y-2">
              {items.map(t => {
                const fromS = STATUS_STYLE[t.from_status] || STATUS_STYLE.unknown;
                const toS = STATUS_STYLE[t.to_status] || STATUS_STYLE.unknown;
                return (
                  <div
                    key={t.id}
                    className="bg-[#0d0d0d] border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-colors"
                    data-testid={`transition-${t.id}`}
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-gray-500 text-xs font-mono">
                        {new Date(t.to_ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className="px-2 py-0.5 rounded text-[11px] font-bold uppercase"
                          style={{ background: fromS.bg, color: fromS.color, border: `1px solid ${fromS.border}` }}
                        >
                          {fromS.label}
                        </span>
                        <ChevronRight size={14} className="text-gray-600" />
                        <span
                          className="px-2 py-0.5 rounded text-[11px] font-bold uppercase"
                          style={{ background: toS.bg, color: toS.color, border: `1px solid ${toS.border}` }}
                        >
                          {toS.label}
                        </span>
                      </div>
                      <div className="ml-auto text-xs text-gray-400">
                        Durata precedente: <span className="font-mono text-gray-200">{formatDuration(t.duration_seconds)}</span>
                      </div>
                    </div>
                    {t.next_categories?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {t.next_categories.map(c => (
                          <span
                            key={c}
                            className="px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide border border-gray-700 text-gray-400"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-2 text-[11px] text-gray-600 font-mono">
                      {formatDateTime(t.from_ts)} → {formatDateTime(t.to_ts)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
